/**
 * API请求日志和性能监控工具
 * 用于记录和监控API请求的性能指标
 */

class ApiMonitor {
	constructor() {
		this.logs = []
		this.maxLogs = 100 // 最多保留100条日志
		this.slowThreshold = 3000 // 慢请求阈值(毫秒)
		this.enabled = process.env.NODE_ENV === 'development' // 仅在开发环境启用
	}

	/**
	 * 记录请求开始
	 */
	startRequest(config) {
		if (!this.enabled) return

		const requestId = this.generateRequestId()
		config.requestId = requestId

		const log = {
			requestId,
			method: config.method?.toUpperCase(),
			url: config.url,
			startTime: new Date(),
			status: 'pending',
			duration: 0,
			retryCount: config.__retryCount || 0
		}

		this.logs.unshift(log)
		this.trimLogs()

		console.log(`[API Start] ${log.method} ${log.url} [${requestId}]`)
	}

	/**
	 * 记录请求成功
	 */
	endRequest(config, response) {
		if (!this.enabled) return

		const log = this.findLog(config.requestId)
		if (!log) return

		log.status = 'success'
		log.endTime = new Date()
		log.duration = log.endTime - log.startTime
		log.statusCode = response?.status || 200

		this.logResult(log)
	}

	/**
	 * 记录请求失败
	 */
	failRequest(config, error) {
		if (!this.enabled) return

		const log = this.findLog(config.requestId)
		if (!log) return

		log.status = 'error'
		log.endTime = new Date()
		log.duration = log.endTime - log.startTime
		log.error = error?.message || 'Unknown error'
		log.statusCode = error?.response?.status || 0

		this.logResult(log)
	}

	/**
	 * 查找日志
	 */
	findLog(requestId) {
		return this.logs.find(log => log.requestId === requestId)
	}

	/**
	 * 记录结果
	 */
	logResult(log) {
		const icon = log.status === 'success' ? '✓' : '✗'
		const durationStr = `${log.duration}ms`
		const slowMark = log.duration > this.slowThreshold ? ' ⚠️ SLOW' : ''

		console.log(
			`[API ${icon}] ${log.method} ${log.url} ${log.statusCode} ${durationStr} [${log.requestId}]${slowMark}`
		)

		// 慢请求警告
		if (log.duration > this.slowThreshold) {
			console.warn(
				`[API Performance] Slow request detected: ${log.method} ${log.url} took ${log.duration}ms`
			)
		}

		// 错误请求详细日志
		if (log.status === 'error') {
			console.error(
				`[API Error] ${log.method} ${log.url}`,
				`\n  Status: ${log.statusCode}`,
				`\n  Duration: ${log.duration}ms`,
				`\n  Error: ${log.error}`
			)
		}
	}

	/**
	 * 生成请求ID
	 */
	generateRequestId() {
		return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
	}

	/**
	 * 修剪日志,保留最新的maxLogs条
	 */
	trimLogs() {
		if (this.logs.length > this.maxLogs) {
			this.logs = this.logs.slice(0, this.maxLogs)
		}
	}

	/**
	 * 获取所有日志
	 */
	getLogs() {
		return [...this.logs]
	}

	/**
	 * 获取统计信息
	 */
	getStats() {
		const total = this.logs.length
		const success = this.logs.filter(log => log.status === 'success').length
		const error = this.logs.filter(log => log.status === 'error').length
		const pending = this.logs.filter(log => log.status === 'pending').length
		const slow = this.logs.filter(log => log.duration > this.slowThreshold).length
		const avgDuration = this.logs.length > 0
			? Math.round(this.logs.reduce((sum, log) => sum + log.duration, 0) / this.logs.length)
			: 0

		return {
			total,
			success,
			error,
			pending,
			slow,
			avgDuration,
			errorRate: total > 0 ? ((error / total) * 100).toFixed(2) + '%' : '0%'
		}
	}

	/**
	 * 清空日志
	 */
	clear() {
		this.logs = []
		console.log('[API Monitor] Logs cleared')
	}

	/**
	 * 打印统计报告
	 */
	printReport() {
		const stats = this.getStats()
		console.log('===== API Monitor Report =====')
		console.log(`Total Requests: ${stats.total}`)
		console.log(`Success: ${stats.success} (${(stats.success / stats.total * 100).toFixed(2)}%)`)
		console.log(`Error: ${stats.error} (${stats.errorRate})`)
		console.log(`Pending: ${stats.pending}`)
		console.log(`Slow Requests: ${stats.slow}`)
		console.log(`Average Duration: ${stats.avgDuration}ms`)
		console.log('=============================')
	}
}

// 创建全局单例
const apiMonitor = new ApiMonitor()

// 导出
export default apiMonitor

// 同时在window上暴露,方便在控制台调试
if (typeof window !== 'undefined') {
	window.apiMonitor = apiMonitor
}
